import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import { Grid } from "@mui/system";
import React, { useEffect, useMemo, useState } from "react";
import { FixedSizeList as List } from "react-window";
import TrendsBDKwMtfilterCard from "./TrendsBDKwMtFilters/TrendsBDKwMtfilterCard";

import { useSelector } from "react-redux";
import { handleTableClickPopupforBars } from "../../helpers/funcs/handleTableClickPopupforBars";
import { useSearchParams } from "react-router-dom";
import { TrendsBreakdownQueryIds } from "../../helpers/funcs/trendsAllQueryIDs";
import TrendsBreakdownPopupForBars from "../TrendsBreakdownPopup/TrendsBreakdownPopupForBars";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../../helpers/funcs/widgetMetricMap";
const formatHeaderLabel = (key: string): string => {
  if (!key) return "";
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const headCells = (metricKey: string): [string, string][] => [
  ["keyword_id", "KeyWord ID"],
  ["keyword_name", "KeyWord Name"],
  ["matchtype", "Match Type"],
  [metricKey || "", formatHeaderLabel(metricKey) || ""],
  ["click_in", "Clicks"],
  ["traffic_source", "Traffic Source"],
  ["adgroup_name", "Ad Group Name"],
  ["adgroup_id", "Ad Group ID"],
  ["campaign_name", "Campaign Name"],
  ["campaign_id", "Campaign ID"],
];

// Row height constant
const ROW_HEIGHT = 53; // Adjust based on your row height
const HEADER_HEIGHT = 56; // Adjust based on your header height
const COLUMN_WIDTHS = [
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
  "10%",
];
// Custom row component for react-window
const TableRowComponent = React.memo(({ index, style, data }: any) => {
  const {
    rows,
    setPopupOpen,
    setSelectedTableDataKWMT,
    searchParams,
    setSearchParams,
    metricKey,
    headCells,
  } = data;

  const row = rows[index];
  if (row.__isPlaceholder) {
    return (
      <div style={style}>
        <TableRow sx={{ display: "flex", height: ROW_HEIGHT }}>
          <TableCell
            colSpan={headCells.length}
            align="center"
            sx={{
              flex: "1 1 auto",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              height: ROW_HEIGHT,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </TableCell>
        </TableRow>
      </div>
    );
  }
  return (
    <div style={style}>
      <TableRow
        sx={{
          display: "flex",
          alignItems: "center",
          height: ROW_HEIGHT,
          minHeight: ROW_HEIGHT,
          "& > *": {
            borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
          },
        }}
      >
        {headCells.map(([key]: [string, any], cellIndex: number) => (
          <TableCell
            key={cellIndex}
            align="left"
            sx={{
              flex: cellIndex === 1 ? "2 1 200px" : "1 1 120px", // Keyword name gets more space
              width: COLUMN_WIDTHS[cellIndex],
              minWidth: COLUMN_WIDTHS[cellIndex],
              maxWidth: COLUMN_WIDTHS[cellIndex],
              height: ROW_HEIGHT,
              minHeight: ROW_HEIGHT,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              padding: "8px 16px",
              cursor:
                key === "keyword_name"
                  ? data.isLoadingBDAds || data.isChildrenAPILoadingBDAds
                    ? "wait"
                    : "pointer"
                  : "default",
              color: key === "keyword_name" ? "#9c27b0" : "inherit",
              // Add hover effect for keyword_name column
              ...(key === "keyword_name" && {
                transition: "all 0.1s ease-in-out",
                "&:hover": {
                  color: "#9c27b0",
                  fontSize: "0.90rem",
                  transform: "scale(1.00)",
                  fontWeight: "500",
                },
              }),
            }}
            onClick={
              key === "keyword_name"
                ? () => {
                    if (data.isLoadingBDAds || data.isChildrenAPILoadingBDAds)
                      return;
                    handleTableClickPopupforBars({
                      keywordName: row.keyword_name,
                      keywordId: row.keyword_id,
                      setSelectedBarData: setSelectedTableDataKWMT,
                      setPopupOpen,
                      searchParams,
                      setSearchParams,
                      section: "Keyword Match Type Table",
                      queryID: TrendsBreakdownQueryIds.KEYWORD_MATCH_TYPE,
                    });
                  }
                : undefined
            }
            data-tracker="491"
            // style={{ cursor: key === "keyword_name" ? "pointer" : "default" }}
          >
            {key === metricKey &&
            row[key] !== undefined &&
            row[key] !== null ? (
              parseFloat(row[key]).toFixed(2)
            ) : (key === "campaign_name" ||
                key === "adgroup_name" ||
                key === "adgroup_id" ||
                key === "campaign_id") &&
              row[key] ? (
              <Tooltip title={row[key]} placement="top" arrow>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row[key]}
                </span>
              </Tooltip>
            ) : (
              row[key] ?? ""
            )}
          </TableCell>
        ))}
      </TableRow>
    </div>
  );
});

TableRowComponent.displayName = "TableRowComponent";

const TrendsBDKwMtTable = () => {
  const {
    keywordMatchTypeDataTrendsBreakdown,
    selectedKeyword,
    selectedMatchTypeFilterBD,
    selectedCampaignFilterBD,
    selectedAdGroupFilterBD,
    selectedTrafficSourceFilterBD,
    isLoadingBDAds,
    isChildrenAPILoadingBDAds,
  } = useSelector((state: any) => state.trends);
  const [orderBy, setOrderBy] = useState<string>("click_in"); // default sort column
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("desc");
  const [searchParams, setSearchParams] = useSearchParams();
  const [popupOpen, setPopupOpen] = useState(false);
  const fullWidgetName: any = searchParams.get("full_widget_name");
  const metricName: any = searchParams.get("metric_name");
  const [selectedTableDataKWMT, setSelectedTableDataKWMT] = useState(null);
  const allRows = useMemo(
    () => keywordMatchTypeDataTrendsBreakdown?.data?.data?.query_output || [],
    [keywordMatchTypeDataTrendsBreakdown]
  );
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderBy(property);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const rows = useMemo(() => {
    let filtered = [...allRows];

    // Filter by keyword search
    if (selectedKeyword) {
      filtered = filtered.filter((row) =>
        row.keyword_name?.toLowerCase().includes(selectedKeyword.toLowerCase())
      );
    }

    // Match Type filter
    if (selectedMatchTypeFilterBD?.length > 0) {
      filtered = filtered.filter((row) =>
        selectedMatchTypeFilterBD.includes(row.matchtype)
      );
    }

    // Traffic Source filter
    if (selectedTrafficSourceFilterBD?.length > 0) {
      filtered = filtered.filter((row) =>
        selectedTrafficSourceFilterBD.includes(row.traffic_source)
      );
    }

    // Campaign filter
    if (selectedCampaignFilterBD?.length > 0) {
      filtered = filtered.filter((row) =>
        selectedCampaignFilterBD.includes(row.campaign_name)
      );
    }

    // Ad Group filter
    if (selectedAdGroupFilterBD?.length > 0) {
      filtered = filtered.filter((row) =>
        selectedAdGroupFilterBD.includes(row.adgroup_name)
      );
    }

    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return orderDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return orderDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    if (filtered.length === 0) {
      return [
        {
          __isPlaceholder: true, // special flag
        },
      ];
    }

    return filtered;
  }, [
    allRows,
    selectedKeyword,
    selectedMatchTypeFilterBD,
    selectedTrafficSourceFilterBD,
    selectedCampaignFilterBD,
    selectedAdGroupFilterBD,
    orderBy,
    orderDirection,
  ]);

  // Calculate container height (max 588px as in original)
  const containerHeight = Math.min(
    588,
    HEADER_HEIGHT + rows.length * ROW_HEIGHT
  );
  const listHeight = containerHeight - HEADER_HEIGHT;

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedTableDataKWMT(null);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("section");
    updatedParams.delete("queryID");
    setSearchParams(updatedParams);
  };

  useEffect(() => {
    const section = searchParams.get("section");
    const queryID = searchParams.get("queryID");

    if (section || queryID) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("section");
      updatedParams.delete("queryID");
      setSearchParams(updatedParams, { replace: true }); // avoid history entry
    }
    // eslint-disable-next-line
  }, []);
  const metricKey =
    fullWidgetName === "Funnel Ratio"
      ? funnelRatioMetricMap[metricName] || ""
      : fullWidgetName === "Profit and First Time Deposit"
      ? profitAndFirstTimeDepositMetricMap[metricName] || ""
      : widgetMetricMap[fullWidgetName];
  const dynamicHeadCells = headCells(metricKey);

  return (
    <>
      <Paper
        sx={{
          boxShadow: "none",
          padding: "8px 16px 16px 16px",
          borderEndEndRadius: "0px",
          borderBottomLeftRadius: "0px",
        }}
      >
        <Grid container alignItems="center" sx={{ mb: 2 }}>
          <Grid size={12} mb={1}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              Breakdown By Keyword & Matchtype
            </Typography>
          </Grid>
          <TrendsBDKwMtfilterCard />
        </Grid>

        <TableContainer
          component={Paper}
          className="PageScantablewrap"
          sx={{
            height: containerHeight,
            overflow: "hidden", // Prevent double scrollbars
          }}
        >
          <Table aria-label="collapsible table" size="small">
            {/* Fixed Header */}
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "white",
              }}
            >
              <TableRow sx={{ display: "flex" }}>
                {dynamicHeadCells.map(([key, label], index) => (
                  <TableCell
                    key={index}
                    align="left"
                    sx={{
                      flex: index === 1 ? "2 1 200px" : "1 1 120px", // Match row cell sizing
                      width: COLUMN_WIDTHS[index],
                      minWidth: COLUMN_WIDTHS[index],
                      maxWidth: COLUMN_WIDTHS[index],
                    }}
                    onClick={() => handleRequestSort(key)}
                  >
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? orderDirection : "asc"}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>

          <List
            height={listHeight}
            width="100%"
            itemCount={rows.length}
            itemSize={ROW_HEIGHT}
            itemData={{
              rows,
              setPopupOpen,
              setSelectedTableDataKWMT,
              searchParams,
              setSearchParams,
              isLoadingBDAds,
              isChildrenAPILoadingBDAds,
              metricKey,
              headCells: dynamicHeadCells,
            }}
            overscanCount={5} // Render 5 extra items outside visible area for smooth scrolling
          >
            {TableRowComponent}
          </List>
        </TableContainer>
      </Paper>
      <TrendsBreakdownPopupForBars
        open={popupOpen}
        onClose={handleClosePopup}
        data={selectedTableDataKWMT}
      />
    </>
  );
};

export default TrendsBDKwMtTable;
